import React from "react";
import ModalShell from "./ModalShell";

export const PRIVACY_URL = "https://myemotionalsupportbot.com/privacy.html";
export const TERMS_URL = "https://myemotionalsupportbot.com/terms.html";

// The full policies live on the public website; the in-app notices point there so the two stay in step.
function PolicyLinks() {
  return (
    <p className="rg-modal-links">
      Read the full{" "}
      <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{" "}
      <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Use</a>.
    </p>
  );
}

// Wording is unchanged from the earlier dialogs; only the look is new. The lawyer review is still pending.
export const FERPA_ITEMS = [
  "All conversations and interactions are stored securely in compliance with FERPA standards",
  "Your personal information and chat data are protected with industry-standard encryption",
  "What we collect: your first name, email address and password (stored securely by our sign-in provider), and the conversations you have with Rigby",
  "Your conversations are encrypted and are never shared with your school or institution",
  "If you joined through an institution, it can see only your enrollment status, activity dates, and aggregate anonymized usage trends",
  "We keep brief security logs (including a shortened network address) for up to 30 days to protect accounts and prevent abuse",
  "Your information will not be shared with third parties without your explicit consent, except as required by law",
];
export const FERPA_EXTRA_ITEM = "Sentiment analysis and analytics are conducted in aggregate form to protect individual privacy";

function FerpaBody({ full }) {
  return (
    <>
      <p>
        Rigby is designed with compliance to the <strong>Family Educational Rights and Privacy Act (FERPA)</strong> in
        mind. FERPA is a federal law that protects the privacy of student education records.
      </p>
      <h3>Data Privacy Commitment</h3>
      <ul>
        {FERPA_ITEMS.map((t) => (
          <li key={t}>{t}</li>
        ))}
        {full ? <li>{FERPA_EXTRA_ITEM}</li> : null}
      </ul>
      <div className="rg-modal-note">
        <b>FERPA Rights Notice</b>
        Under FERPA, you have the right to inspect and review your education records, request amendments to records you
        believe are inaccurate, and control the disclosure of personally identifiable information.
        {full
          ? " For more information about your FERPA rights, contact your university's registrar or student services office."
          : ""}
      </div>
      <PolicyLinks />
    </>
  );
}

export function FerpaInfoModal({ onClose }) {
  return (
    <ModalShell
      title="FERPA and your privacy"
      subtitle="Your privacy and educational records are protected"
      onClose={onClose}
      footer={
        <button type="button" data-autofocus className="rg-modal-btn rg-modal-btn--primary" onClick={onClose}>
          I understand
        </button>
      }
    >
      <FerpaBody full />
    </ModalShell>
  );
}

// First-sign-in consent. `copy` comes from consentCopy() so only Google accounts hear about Google.
export function FerpaConsentModal({ copy, busy, onCancel, onAccept }) {
  return (
    <ModalShell
      title="FERPA and your privacy"
      subtitle={copy.subtitle}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="rg-modal-btn rg-modal-btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            data-autofocus
            className="rg-modal-btn rg-modal-btn--primary"
            onClick={onAccept}
            disabled={busy}
          >
            {busy ? "Signing in..." : copy.button}
          </button>
        </>
      }
    >
      <FerpaBody full={false} />
    </ModalShell>
  );
}

export function TermsModal({ onClose }) {
  return (
    <ModalShell
      title="Disclaimer and legal notice"
      subtitle="Important information about using Rigby emotional support chatbot"
      onClose={onClose}
      footer={
        <button type="button" data-autofocus className="rg-modal-btn rg-modal-btn--primary" onClick={onClose}>
          Return to chat
        </button>
      }
    >
      <div className="rg-modal-alert">
        <b>Rigby is not a therapist.</b>
        <p>
          This is an educational capstone project and does not provide medical advice, professional therapy, or crisis
          intervention services.
        </p>
      </div>

      <h3>Rigby is not a licensed therapist or medical professional</h3>
      <p>
        Rigby is an AI-powered emotional support chatbot developed as part of a semester-long capstone project at the
        New Jersey Institute of Technology (NJIT). While Rigby can provide empathetic conversation and general emotional
        support, it:
      </p>
      <ul>
        <li>Does NOT provide professional mental health counseling, therapy, or medical advice</li>
        <li>Does NOT diagnose mental health conditions</li>
        <li>Does NOT prescribe treatment or medication</li>
        <li>Cannot replace in-person care from licensed mental health professionals</li>
        <li>Is NOT intended for crisis intervention or emergency situations</li>
      </ul>

      <h3>If you are experiencing a mental health crisis or emergency</h3>
      <ul>
        <li><strong>Call 988</strong> (Suicide &amp; Crisis Lifeline) - Available 24/7</li>
        <li><strong>Text "HELLO" to 741741</strong> (Crisis Text Line)</li>
        <li><strong>Call 911</strong> for immediate emergency assistance</li>
        <li><strong>Contact your university counseling center immediately</strong></li>
      </ul>
      <PolicyLinks />
    </ModalShell>
  );
}

export function CrisisModal({ onClose }) {
  return (
    <ModalShell title="You're not alone" subtitle="If you're in crisis, please reach out right now:" tone="care" onClose={onClose}
      footer={
        <button type="button" data-autofocus className="rg-modal-btn rg-modal-btn--primary" onClick={onClose}>
          I understand, return to chat
        </button>
      }
    >
      <ul className="rg-modal-resources">
        <li>
          <b>University Counseling &amp; Wellness Center</b>
          <span>(customized per institution before go-live)</span>
        </li>
        <li>
          <b>988 Suicide &amp; Crisis Lifeline</b>
          <span>Call or text <strong>988</strong>, available 24/7</span>
        </li>
        <li>
          <b>Emergency</b>
          <span>Call <strong>911</strong> or go to the nearest emergency room</span>
        </li>
      </ul>
    </ModalShell>
  );
}

// A short message with one or two actions. Used for the app's own notices and the guest limit message.
export function MessageModal({ title, body, actions, onClose, children }) {
  return (
    <ModalShell
      title={title}
      onClose={onClose}
      footer={actions}
    >
      {body ? <p style={{ whiteSpace: "pre-line" }}>{body}</p> : null}
      {children}
    </ModalShell>
  );
}
